import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Download, File, Loader, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { downloadFileFromStore } from '../../api/action/s3Bucket';
import { DOMAIN_TYPES } from '../../domain/Domain';
import {
    formatPhoneNumber as formatSharedPhoneNumber
} from '../../utils/format';
import { getBestReceiptOrder, getOrderLineItems } from '../LandingPage/FlexOfficeDomain/flex-new/bookings/utils/orderUtils';
import { formatReceiptPaymentStatus, resolveReceiptStatus } from '../LandingPage/FlexOfficeDomain/flex-new/bookings/utils/receiptStatus';
import ReceiptDocument from './components/ReceiptDocument';

const getDownloadTheme = (domainType) => {
    if (domainType === DOMAIN_TYPES.FLEX_OFFICE) {
        return {
            accent: 'var(--flex-primary, #2563EB)',
            border: 'var(--flex-primary-muted, #BFDBFE)',
            panel: 'var(--flex-primary-soft, #EFF6FF)',
            panelAlt: 'var(--flex-surface, #F8FBFF)',
            text: 'var(--flex-primary, #1D4ED8)'
        };
    }

    if (domainType === DOMAIN_TYPES.RETAIL) {
        return {
            accent: '#EAAA3A',
            border: '#F5D39B',
            panel: '#FFF7E8',
            panelAlt: '#FFFDF8',
            text: '#B45309'
        };
    }

    return {
        accent: 'var(--education-primary, #746EAC)',
        border: 'var(--education-primary-muted, #DDD8F2)',
        panel: 'var(--education-primary-soft, #F6F4FF)',
        panelAlt: 'var(--education-surface, #FBFAFF)',
        text: 'var(--education-primary-dark, #5B4E9C)'
    };
};

const hasReceiptUserDetails = (user) => Boolean(
    user?.firstName
    || user?.lastName
    || user?.email
    || user?.phoneNumber
    || user?.addressList?.length
);

const resolveReceiptUser = (...users) => (
    users.find(hasReceiptUserDetails) || users.find(Boolean) || {}
);

const PAYMENT_METHOD_LABELS = {
    ACH: 'ACH Transfer',
    BANK_TRANSFER: 'Bank Transfer',
    CARD: 'Card',
    CASH: 'Cash',
    CASH_ON_DELIVERY: 'Cash on Delivery',
    CHECK: 'Cheque',
    CHEQUE: 'Cheque',
    COD: 'Cash on Delivery',
    CREDIT_CARD: 'Credit Card',
    CRYPTO: 'Crypto',
    DEBIT_CARD: 'Debit Card',
    NET_BANKING: 'Net Banking',
    ONLINE: 'Online Payment',
    OTHER: 'Other',
    PAYPAL: 'PayPal',
    RAZORPAY: 'Razorpay',
    STRIPE: 'Stripe',
    UPI: 'UPI',
    WALLET: 'Wallet',
    WIRE: 'Wire Transfer',
    ZELLE: 'Zelle'
};

const BLANK_PAYMENT_VALUES = new Set(['', 'N/A', 'NA', 'NONE', 'NULL', 'UNDEFINED', '-', '—']);
const base64ImageCache = new Map();

// Booking start/end dates (localStartDate/localEndDate) are already wall-clock
// values in the company's timezone, with no UTC offset. Running them through
// formatDateInCompanyTimeZone would re-apply the company's offset on top of the
// browser's local parsing and shift the displayed date/time, so they're
// rendered as-is instead.
const formatBookingDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return Number.isNaN(date.getTime())
        ? ''
        : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatBookingTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return Number.isNaN(date.getTime())
        ? ''
        : date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

const formatPhoneWithCountryCode = (countryCode, phoneNumber) => {
    const normalizedPhone = String(phoneNumber || '').trim();
    if (!normalizedPhone) return '';
    return formatSharedPhoneNumber(normalizedPhone, countryCode || 'US');
};

const formatPaymentMethodLabel = (value) => {
    if (!value) return 'Cash';

    const normalizedValue = String(value).trim().toUpperCase().replace(/[\s-]/g, '_');
    if (BLANK_PAYMENT_VALUES.has(normalizedValue)) return 'Cash';

    return PAYMENT_METHOD_LABELS[normalizedValue]
        || String(value)
            .trim()
            .replace(/_/g, ' ')
            .toLowerCase()
            .replace(/\b\w/g, (char) => char.toUpperCase());
};

const buildAddressValue = (address) => {
    if (!address) return '';
    return [
        address.streetName1,
        address.streetName2,
        [address.city, address.state, address.zipcode].filter(Boolean).join(', '),
        address.country
    ].filter(Boolean).join('\n');
};

const getResolvedCompany = (invoice, order, companyDetails) => {
    const invoiceCompany = invoice?.company || {};
    const orderCompany = order?.company || {};

    return {
        ...(companyDetails || {}),
        ...orderCompany,
        ...invoiceCompany,
        logoImageContent: {
            ...(companyDetails?.logoImageContent || {}),
            ...(orderCompany?.logoImageContent || {}),
            ...(invoiceCompany?.logoImageContent || {})
        },
        contactPerson: {
            ...(companyDetails?.contactPerson || {}),
            ...(orderCompany?.contactPerson || {}),
            ...(invoiceCompany?.contactPerson || {})
        },
        address: invoiceCompany?.address || orderCompany?.address || companyDetails?.address || null,
        domain: invoiceCompany?.domain || orderCompany?.domain || companyDetails?.domain || null
    };
};

const getCompanyLogoSource = (company) =>
    company?.logoImageContent?.displayLink
    || company?.logoImageContent?.storedLink
    || company?.logoLocation
    || company?.logoUrl
    || null;

const extractS3ObjectKey = (value) => {
    const normalizedValue = String(value || '').trim();
    if (!normalizedValue) return '';

    if (!/^https?:\/\//i.test(normalizedValue)) {
        return normalizedValue.replace(/^\/+/, '');
    }

    try {
        const parsedUrl = new URL(normalizedValue);
        return decodeURIComponent(parsedUrl.pathname.replace(/^\/+/, ''));
    } catch {
        return '';
    }
};

const getCompanyLogoExportKey = (company) => {
    const possibleSources = [
        company?.logoImageContent?.storedLink,
        company?.logoLocation,
        company?.logoUrl,
        company?.logoImageContent?.displayLink
    ];

    return possibleSources.map(extractS3ObjectKey).find(Boolean) || null;
};

const isDataUrl = (value) => /^data:/i.test(String(value || '').trim());

const readBlobAsDataUrl = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error || new Error('Failed to read image blob'));
    reader.readAsDataURL(blob);
});

const getBase64Image = async (key, dispatch) => {
    const normalizedKey = String(key || '').trim();
    if (!normalizedKey) return null;
    if (isDataUrl(normalizedKey)) return normalizedKey;

    if (!base64ImageCache.has(normalizedKey)) {
        base64ImageCache.set(
            normalizedKey,
            (async () => {
                try {
                    const blob = await dispatch(downloadFileFromStore(normalizedKey));
                    if (!blob) return null;
                    return await readBlobAsDataUrl(blob);
                } catch {
                    return null;
                }
            })()
        );
    }

    return base64ImageCache.get(normalizedKey);
};

const waitForNextPaint = () => new Promise((resolve) => {
    window.requestAnimationFrame(() => {
        window.requestAnimationFrame(resolve);
    });
});

const waitForImageElement = async (img) => {
    if (!img) return;

    if (img.complete) {
        if (img.naturalWidth > 0 && typeof img.decode === 'function') {
            try {
                await img.decode();
            } catch {
                // Ignore decode failures for cached images that already rendered.
            }
        }
        return;
    }

    await new Promise((resolve) => {
        const timeoutId = window.setTimeout(() => {
            img.removeEventListener('load', done);
            img.removeEventListener('error', done);
            resolve();
        }, 8000);

        const done = () => {
            window.clearTimeout(timeoutId);
            img.removeEventListener('load', done);
            img.removeEventListener('error', done);
            resolve();
        };

        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
    });

    if (typeof img.decode === 'function' && img.complete && img.naturalWidth > 0) {
        try {
            await img.decode();
        } catch {
            // Ignore decode failures after the image load event.
        }
    }
};

const waitForNodeImages = async (node) => {
    const images = Array.from(node?.querySelectorAll?.('img') || []);
    if (!images.length) return;
    await Promise.all(images.map(waitForImageElement));
};

const getScheduledPeriod = (lineItem) => lineItem?.scheduledProduct?.period || null;

const getBookingDateRange = (lineItems) => {
    const period = getScheduledPeriod(lineItems?.[0]);
    const effectiveDate = period?.effectiveDate || {};

    return {
        startDate: effectiveDate?.localStartDate || period?.effectiveStartDate || '',
        endDate: effectiveDate?.localEndDate || period?.effectiveEndDate || '',
        timeZone: effectiveDate?.timeZone || ''
    };
};

const getBookingDurationLabel = (period) => {
    if (!period) return '';

    if (period?.duration && period?.durationType) {
        const normalizedType = String(period.durationType).toLowerCase();
        const label = normalizedType.charAt(0).toUpperCase() + normalizedType.slice(1);
        return `${period.duration} ${label}${Number(period.duration) === 1 ? '' : 's'}`;
    }

    const effectiveDate = period?.effectiveDate || {};
    const startDate = effectiveDate?.localStartDate || period?.effectiveStartDate;
    const endDate = effectiveDate?.localEndDate || period?.effectiveEndDate;

    if (!startDate || !endDate) return '';

    try {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffHours = (end - start) / (1000 * 60 * 60);
        if (!Number.isFinite(diffHours) || diffHours <= 0) return '';
        if (diffHours < 1) {
            const minutes = Math.round(diffHours * 60);
            return `${minutes} min${minutes === 1 ? '' : 's'}`;
        }
        const roundedHours = Math.round(diffHours * 100) / 100;
        return `${roundedHours} hr${roundedHours === 1 ? '' : 's'}`;
    } catch {
        return '';
    }
};

const getPaymentDetails = (invoice, order) => {
    const primaryTransaction = order?.transactionList?.[0] || invoice?.order?.transactionList?.[0] || {};
    const paymentMethod = (() => {
        const paymentMethodData = primaryTransaction?.paymentMethod;
        if (
            paymentMethodData?.paymentMethodType === 'BANK_ACCOUNT'
            && String(paymentMethodData?.name || '').toLowerCase().includes('zelle')
        ) {
            return 'ZELLE';
        }

        return paymentMethodData?.paymentMethodType || invoice?.paymentMethod || '';
    })();

    return {
        paymentMethod,
        paymentStatus: invoice?.paymentStatus || primaryTransaction?.currentStatus || '',
        transactionId: invoice?.transactionId || primaryTransaction?.transactionId || primaryTransaction?.referenceId || ''
    };
};

const getLineItemRate = (item) => {
    const quantity = Number(item?.quantity) || 1;
    const directRate = item?.product?.price?.priceAmt
        || item?.scheduledProduct?.product?.price?.priceAmt
        || item?.priceAmt
        || item?.rate;

    if (directRate !== undefined && directRate !== null && directRate !== '') {
        return Number(directRate) || 0;
    }

    const totalAmount = Number(item?.totalAmount);
    if (Number.isFinite(totalAmount) && quantity > 0) {
        return totalAmount / quantity;
    }

    return 0;
};

const getLineItemDescription = (item, { isRetailDomain, isAcademyDomain, companyOrCode }) => {
    const product = item?.scheduledProduct?.product || item?.product || {};
    const parts = [];
    const groupedSessionCount = Number(item?.groupedSessionCount) || 0;
    const sessionDuration = Number(item?.scheduledProduct?.period?.duration) || 0;

    if (product?.category?.name) {
        parts.push(product.category.name);
    }

    if (!isRetailDomain && !isAcademyDomain) {
        if (groupedSessionCount > 1) {
            parts.push(`${groupedSessionCount} sessions`);
            if (sessionDuration > 0) {
                parts.push(`${sessionDuration} hour${sessionDuration === 1 ? '' : 's'} each`);
            }
        }

        const { startDate, endDate } = getBookingDateRange([item]);
        const bookingDate = formatBookingDate(startDate, companyOrCode);
        const bookingTime = [formatBookingTime(startDate, companyOrCode), formatBookingTime(endDate, companyOrCode)].filter(Boolean).join(' - ');
        if (bookingDate) parts.push(bookingDate);
        if (bookingTime) parts.push(bookingTime);
    }

    if (isRetailDomain && product?.sku) {
        parts.push(`SKU: ${product.sku}`);
    }

    return parts.filter(Boolean).join(' | ');
};

const buildReceiptProps = ({ invoice, order, user, companyDetails, documentType, exportCompanyLogo }) => {
    const receiptOrder = getBestReceiptOrder(order, invoice?.order);
    const resolvedCompany = getResolvedCompany(invoice, receiptOrder, companyDetails);
    const receiptUser = resolveReceiptUser(invoice?.user, receiptOrder?.user, invoice?.order?.user, user);
    const domainType = resolvedCompany?.domain?.type || invoice?.company?.domain?.type || localStorage.getItem('domainType');
    const isFlexOfficeDomain = domainType === DOMAIN_TYPES.FLEX_OFFICE;
    const isRetailDomain = domainType === DOMAIN_TYPES.RETAIL;
    const isAcademyDomain = domainType === DOMAIN_TYPES.EDUCATION || domainType === DOMAIN_TYPES.ASSESSMENT;
    const lineItems = getOrderLineItems(receiptOrder).length > 0 ? getOrderLineItems(receiptOrder) : (invoice?.items || []);
    const { paymentMethod, paymentStatus, transactionId } = getPaymentDetails(invoice, receiptOrder);
    const { startDate, endDate, timeZone } = getBookingDateRange(lineItems);
    const companyAddress = buildAddressValue(resolvedCompany?.address);
    const companyPhone = formatPhoneWithCountryCode(
        resolvedCompany?.contactPerson?.countryCode,
        resolvedCompany?.contactPerson?.phoneNumber
    );
    const customerPhone = formatPhoneWithCountryCode(receiptUser?.countryCode, receiptUser?.phoneNumber);
    const customerName = receiptUser?.firstName || receiptUser?.lastName
        ? `${receiptUser?.firstName || ''} ${receiptUser?.lastName || ''}`.trim()
        : receiptUser?.email || 'N/A';
    const status = resolveReceiptStatus(invoice, receiptOrder).label;
    const paymentStatusLabel = formatReceiptPaymentStatus(paymentStatus, invoice, receiptOrder);
    const subtotal = Number(receiptOrder?.subtotalAmount ?? receiptOrder?.totalBeforTax ?? receiptOrder?.totalAmount ?? invoice?.order?.totalAmount ?? 0) || 0;
    const discountAmount = Number(receiptOrder?.totalDiscountAmt ?? invoice?.discountAmount ?? 0) || 0;
    const total = Number(receiptOrder?.totalAmtAfterDiscount ?? receiptOrder?.dueAmount ?? receiptOrder?.totalAmount ?? invoice?.order?.totalAmount ?? invoice?.totalAmount ?? 0) || 0;

    const items = lineItems.map((item, index) => {
        const product = item?.scheduledProduct?.product || item?.product || {};
        const qty = Number(item?.quantity) || 1;
        const rate = getLineItemRate(item);

        return {
            id: item?.primaryId || item?.id || `receipt-item-${index}`,
            desc: product?.name || item?.name || item?.scheduledProduct?.name || 'Unnamed Item',
            description: getLineItemDescription(item, { isRetailDomain, isAcademyDomain, companyOrCode: resolvedCompany }),
            qty,
            rate
        };
    });

    return {
        documentLabel: documentType || 'Receipt',
        receiptNumber: invoice?.number || invoice?.id || '',
        customer: customerName,
        customerEmail: receiptUser?.email || '',
        customerPhone,
        status,
        paymentMethod: formatPaymentMethodLabel(paymentMethod),
        paymentStatus: paymentStatusLabel,
        transactionId,
        bookingDate: formatBookingDate(startDate, resolvedCompany),
        bookingTime: formatBookingTime(startDate, resolvedCompany),
        bookingEndTime: formatBookingTime(endDate, resolvedCompany),
        bookingDuration: isFlexOfficeDomain || isAcademyDomain ? '' : getBookingDurationLabel(getScheduledPeriod(lineItems?.[0])),
        bookingTimeZone: timeZone || '',
        items,
        subtotal,
        discountAmount,
        discountLabel: discountAmount > 0 ? 'Discount' : '',
        total,
        memo: invoice?.memo || '',
        notes: invoice?.notes || receiptOrder?.notes || '',
        companyName: resolvedCompany?.name || companyDetails?.name || '',
        companyAddress,
        companyEmail: resolvedCompany?.contactPerson?.email || companyDetails?.contactPerson?.email || '',
        companyPhone,
        companyLogo: exportCompanyLogo,
        companyContext: resolvedCompany,
        themeVariant: isFlexOfficeDomain ? 'flexOffice' : 'retail'
    };
};

const InvoiceDownload = ({
    invoice,
    order,
    user,
    documentRef,
    documentType,
    onClose,
    isLoading = false,
    error = null
}) => {
    const dispatch = useDispatch();
    const companyState = useSelector((state) => state.companyReducer.company);
    const [loading, setLoading] = useState(Boolean(isLoading));
    const [exportError, setExportError] = useState(error || null);
    const [exportCompanyLogo, setExportCompanyLogo] = useState(null);
    const pdfGenerationRef = useRef(false);
    const receiptDocRef = useRef(null);
    const domainType = invoice?.company?.domain?.type
        || order?.company?.domain?.type
        || companyState?.domain?.type
        || localStorage.getItem('domainType');
    const downloadTheme = getDownloadTheme(domainType);
    const resolvedCompany = useMemo(
        () => getResolvedCompany(invoice, order, companyState),
        [companyState, invoice, order]
    );
    const exportLogoKey = useMemo(
        () => getCompanyLogoExportKey(resolvedCompany),
        [resolvedCompany]
    );
    const exportReceiptProps = useMemo(
        () => buildReceiptProps({
            invoice,
            order,
            user,
            companyDetails: companyState,
            documentType,
            exportCompanyLogo
        }),
        [companyState, documentType, exportCompanyLogo, invoice, order, user]
    );

    useEffect(() => {
        setLoading(Boolean(isLoading));
    }, [isLoading]);

    useEffect(() => {
        setExportError(error || null);
    }, [error]);

    useEffect(() => {
        let isActive = true;

        if (!exportLogoKey) {
            setExportCompanyLogo(null);
            return () => {
                isActive = false;
            };
        }

        if (isDataUrl(exportLogoKey)) {
            setExportCompanyLogo(exportLogoKey);
            return () => {
                isActive = false;
            };
        }

        setExportCompanyLogo(null);

        getBase64Image(exportLogoKey, dispatch).then((base64Logo) => {
            if (isActive) {
                setExportCompanyLogo(base64Logo || null);
            }
        });

        return () => {
            isActive = false;
        };
    }, [dispatch, exportLogoKey]);

    const ensureExportLogoReady = async () => {
        if (!exportLogoKey) return true;

        const base64Logo = isDataUrl(exportLogoKey) ? exportLogoKey : (await getBase64Image(exportLogoKey, dispatch));
        if (!base64Logo) return false;

        if (base64Logo !== exportCompanyLogo) {
            setExportCompanyLogo(base64Logo);
        }

        await waitForNextPaint();
        return true;
    };

    const createExportReceiptNode = async () => {
        const sourceNode = receiptDocRef.current || documentRef?.current;
        if (!sourceNode) return null;

        const hasVisibleCompanyLogo = Boolean(getCompanyLogoSource(resolvedCompany));
        if (hasVisibleCompanyLogo) {
            const isLogoReady = await ensureExportLogoReady();
            if (!isLogoReady) return null;
        }

        await waitForNodeImages(sourceNode);

        const wrapper = document.createElement('div');
        const clone = sourceNode.cloneNode(true);
        const width = sourceNode.scrollWidth || 794;
        const sourceImages = Array.from(sourceNode.querySelectorAll('img'));
        const cloneImages = Array.from(clone.querySelectorAll('img'));

        cloneImages.forEach((img, index) => {
            const sourceImg = sourceImages[index];
            const resolvedSrc = sourceImg?.currentSrc || sourceImg?.getAttribute('src') || img.getAttribute('src');
            if (resolvedSrc) {
                img.setAttribute('src', resolvedSrc);
            }
            img.setAttribute('loading', 'eager');
            img.setAttribute('decoding', 'sync');
        });

        Object.assign(wrapper.style, {
            background: '#FFFFFF',
            left: '0',
            opacity: '0',
            overflow: 'visible',
            pointerEvents: 'none',
            position: 'fixed',
            top: '0',
            width: `${width}px`,
            zIndex: '-1'
        });

        wrapper.setAttribute('aria-hidden', 'true');
        wrapper.appendChild(clone);
        document.body.appendChild(wrapper);

        await waitForNextPaint();
        await waitForNodeImages(clone);

        return {
            cleanup: () => wrapper.remove(),
            node: clone,
            width
        };
    };

    const generatePDF = async () => {
        if (pdfGenerationRef.current || loading) return;

        pdfGenerationRef.current = true;
        setLoading(true);
        setExportError(null);

        try {
            const fileName = `${documentType || 'Receipt'}_${invoice?.number || invoice?.id || 'document'}_${new Date().toISOString().split('T')[0]}.pdf`;
            const exportTarget = await createExportReceiptNode();

            if (!exportTarget) {
                throw new Error('Unable to prepare the receipt for download.');
            }

            try {
                const canvas = await html2canvas(exportTarget.node, {
                    backgroundColor: '#FFFFFF',
                    imageTimeout: 15000,
                    logging: false,
                    scale: 2,
                    useCORS: true,
                    windowWidth: exportTarget.width
                });

                const imgData = canvas.toDataURL('image/jpeg', 0.92);
                const pdf = new jsPDF({
                    format: 'a4',
                    orientation: 'portrait',
                    unit: 'mm',
                    compress: true
                });

                const pageWidth = pdf.internal.pageSize.getWidth();
                const pageHeight = pdf.internal.pageSize.getHeight();
                const imageHeight = (canvas.height * pageWidth) / canvas.width;

                let heightLeft = imageHeight;
                let offsetY = 0;

                pdf.addImage(imgData, 'JPEG', 0, offsetY, pageWidth, imageHeight, undefined, 'MEDIUM');
                heightLeft -= pageHeight;

                while (heightLeft > 0) {
                    offsetY = heightLeft - imageHeight;
                    pdf.addPage();
                    pdf.addImage(imgData, 'JPEG', 0, offsetY, pageWidth, imageHeight, undefined, 'MEDIUM');
                    heightLeft -= pageHeight;
                }

                pdf.save(fileName);
                onClose?.();
            } finally {
                exportTarget.cleanup();
            }
        } catch (downloadError) {
            const message = downloadError?.message || 'Failed to generate PDF. Please try again.';
            setExportError(message);
        } finally {
            setLoading(false);
            pdfGenerationRef.current = false;
        }
    };

    return (
        <>
            <div
                style={{
                    position: 'fixed',
                    left: '-10000px',
                    top: '0',
                    width: '794px',
                    opacity: 0,
                    pointerEvents: 'none'
                }}
                aria-hidden="true"
            >
                <ReceiptDocument
                    ref={receiptDocRef}
                    {...exportReceiptProps}
                />
            </div>

            <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                <div className="bg-white w-full max-w-md rounded-2xl shadow-xl overflow-hidden" style={{ border: `1px solid ${downloadTheme.border}` }}>
                    <div className="p-6 border-b backdrop-blur-sm" style={{ borderColor: downloadTheme.border, background: `linear-gradient(to right, ${downloadTheme.panel}, ${downloadTheme.panelAlt})` }}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div
                                    className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg"
                                    style={{ background: `linear-gradient(to bottom right, ${downloadTheme.accent}, ${downloadTheme.text})` }}
                                >
                                    <Download className="w-6 h-6 text-white drop-shadow-sm" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold tracking-tight" style={{ color: downloadTheme.text }}>Download {documentType}</h3>
                                    <p className="text-sm font-semibold" style={{ color: downloadTheme.text }}>#{invoice?.number || invoice?.id}</p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 rounded-xl transition-all duration-200 hover:scale-110 active:scale-95"
                                style={{ backgroundColor: 'transparent' }}
                                disabled={loading}
                            >
                                <X size={20} style={{ color: downloadTheme.text }} />
                            </button>
                        </div>
                    </div>

                <div className="p-6 bg-white">
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-bold mb-4 tracking-wider" style={{ color: downloadTheme.text }}>
                                Download Format
                            </label>
                            <div className="flex justify-center">
                                <div
                                    className="p-8 w-48 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                                    style={{
                                        background: `linear-gradient(to bottom right, ${downloadTheme.panel}, #FFFFFF)`,
                                        border: `2px solid ${downloadTheme.accent}`
                                    }}
                                >
                                    <div
                                        className="w-16 h-16 mx-auto mb-4 rounded-xl flex items-center justify-center shadow-md"
                                        style={{ background: `linear-gradient(to bottom right, ${downloadTheme.accent}, ${downloadTheme.text})` }}
                                    >
                                        <File className="w-8 h-8 text-white" />
                                    </div>
                                    <span className="text-lg font-bold block text-center" style={{ color: downloadTheme.text }}>
                                        PDF
                                    </span>
                                    <p className="text-xs font-medium mt-2 text-center" style={{ color: downloadTheme.text }}>
                                        Best for printing and sharing
                                    </p>
                                </div>
                            </div>

                            {exportError ? (
                                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                                    {exportError}
                                </div>
                            ) : null}
                        </div>
                    </div>

                    <div className="p-6 border-t" style={{ borderColor: downloadTheme.border, background: `linear-gradient(to right, ${downloadTheme.panel}, ${downloadTheme.panelAlt})` }}>
                        <div className="flex gap-3">
                            <button
                                onClick={onClose}
                                className="flex-1 px-5 py-3 border-2 font-bold text-sm tracking-wider rounded-xl transition-all duration-200 hover:scale-105 hover:shadow-md"
                                style={{ borderColor: downloadTheme.border, color: downloadTheme.text }}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={generatePDF}
                                disabled={loading}
                                className="flex-1 px-5 py-3 text-white font-bold text-sm tracking-wider rounded-xl flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:shadow-lg"
                                style={{ background: `linear-gradient(to right, ${downloadTheme.accent}, ${downloadTheme.text})` }}
                            >
                                {loading ? (
                                    <>
                                        <Loader className="w-5 h-5 animate-spin" />
                                        Preparing PDF...
                                    </>
                                ) : (
                                    <>
                                        <Download size={18} />
                                        Download
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            </div>
        </>
    );
};

export default InvoiceDownload;
